import clsx from "clsx"
import { MutableRefObject, createContext, useEffect, useRef } from "react"

import useHasAccess from "@hooks/useHasAccess"
import useUser from "@hooks/useUser"

import "./BryntumBaseRoopairsTheme.scss"
import { JobEventDrag } from "./JobEventDrag"
import styles from "./JobTimelineView.module.scss"
import { JobTimelineBryntumGrid, JobTimelineBryntumSchedulerPro } from "./JobTimelineView.types"
import JobTimelineViewHorizontalScheduler from "./JobTimelineViewHorizontalScheduler"
import JobTimelineViewHorizontalUnscheduledJobs from "./JobTimelineViewHorizontalUnscheduledJobs"
import "./JobTimelineViewStyles/JobTimelineViewCells.scss"
import "./JobTimelineViewStyles/JobTimelineViewColumnLines.scss"
import "./JobTimelineViewStyles/JobTimelineViewControlsDatePicker.scss"
import "./JobTimelineViewStyles/JobTimelineViewEvent.scss"
import "./JobTimelineViewStyles/JobTimelineViewGrid.scss"
import "./JobTimelineViewStyles/JobTimelineViewTechniciansBar.scss"
import "./JobTimelineViewStyles/JobTimelineViewTimeAxisHeader.scss"
import JobTimelineViewVerticalScheduler from "./JobTimelineViewVerticalScheduler"
import JobTimelineViewVerticalUnscheduledJobs from "./JobTimelineViewVerticalUnscheduledJobs"
import useJobTimelineViewStates from "./hooks/useJobTimelineViewStates"

interface JobTimelineViewContextProps {
    schedulerPro: MutableRefObject<JobTimelineBryntumSchedulerPro | null>
    unscheduledJobsGrid: MutableRefObject<JobTimelineBryntumGrid | null>
    unscheduledJobsGridDrag: MutableRefObject<JobEventDrag | null>
}

export const JobTimelineViewContext = createContext<JobTimelineViewContextProps | undefined>(undefined)

export default function JobTimelineView() {
    const { timelineViewOrientation, setIsSchedulerConfigured } = useJobTimelineViewStates()
    const { user } = useUser()

    useEffect(() => {
        return () => {
            setIsSchedulerConfigured(false)
        }
    }, [])

    if (!user) {
        return null
    } else {
        if (timelineViewOrientation === "horizontal") {
            return <HorizontalTimeline />
        } else {
            return <VerticalTimeline />
        }
    }
}

const HorizontalTimeline = () => {
    const { jobCardStyle, timeFrameType } = useJobTimelineViewStates()
    const { hasFlag } = useHasAccess()

    const schedulerProRef = useRef<JobTimelineBryntumSchedulerPro>(null)
    const unscheduledJobsGridRef = useRef<JobTimelineBryntumGrid>(null)
    const unscheduledJobsGridDragRef = useRef<JobEventDrag>(null)

    // This keeps the unscheduled jobs panel in sync with the scheduler style
    useEffect(() => {
        if (unscheduledJobsGridDragRef.current) {
            unscheduledJobsGridDragRef.current.schedulerIsInCompactMode = jobCardStyle === "compact"
        }
    }, [jobCardStyle])

    return (
        <JobTimelineViewContext.Provider
            value={{
                schedulerPro: schedulerProRef,
                unscheduledJobsGrid: unscheduledJobsGridRef,
                unscheduledJobsGridDrag: unscheduledJobsGridDragRef,
            }}
        >
            <div
                className={clsx(styles.base, "job-timeline-view jsJobTimelineView", {
                    [styles.timelineViewRestricted]: hasFlag("restricted_to_assigned_jobs"),
                    "job-timeline-view--time-frame-day": timeFrameType === "day",
                    "job-timeline-view--time-frame-three-days": timeFrameType === "threeDays",
                    "job-timeline-view--time-frame-week": timeFrameType === "week",
                })}
            >
                <JobTimelineViewHorizontalScheduler />
                <JobTimelineViewHorizontalUnscheduledJobs />
            </div>
        </JobTimelineViewContext.Provider>
    )
}

const VerticalTimeline = () => {
    const { timeFrameType } = useJobTimelineViewStates()

    const { hasFlag } = useHasAccess()

    const schedulerProRef = useRef<JobTimelineBryntumSchedulerPro>(null)
    const unscheduledJobsGridRef = useRef<JobTimelineBryntumGrid>(null)
    const unscheduledJobsGridDragRef = useRef<JobEventDrag>(null)

    return (
        <JobTimelineViewContext.Provider
            value={{
                schedulerPro: schedulerProRef,
                unscheduledJobsGrid: unscheduledJobsGridRef,
                unscheduledJobsGridDrag: unscheduledJobsGridDragRef,
            }}
        >
            <div
                className={clsx(styles.base, "job-timeline-view jsJobTimelineView", {
                    [styles.timelineViewRestricted]: hasFlag("restricted_to_assigned_jobs"),
                    "job-timeline-view--time-frame-day": timeFrameType === "day",
                    "job-timeline-view--time-frame-three-days": timeFrameType === "threeDays",
                })}
            >
                <JobTimelineViewVerticalScheduler />
                <JobTimelineViewVerticalUnscheduledJobs />
            </div>
        </JobTimelineViewContext.Provider>
    )
}
